import { useEffect, useState, useCallback, useRef } from 'react';

import dayjs from 'utils/extendedDayJS';
import { useInterval } from 'hooks/useInterval';
import { useToaster } from 'hooks/useToaster';
import { CSV_QUEUE_STATUS, getCSVReportById, generateCSV } from 'utils/csv';

export interface InsightsCsvParams {
  userId: string;
  startDate: Date | null;
  endDate: Date | null;
}

export type ExportParams = number | InsightsCsvParams | null | undefined;

interface UseCsvReportDownloadOptions {
  type: number;
  timezoneCode?: string;
  exportParams?: number | null;
  pollInterval?: number;
  onSuccess?: () => void;
  onError?: (errorMessage: string) => void;
}

interface UseCsvReportDownloadValue {
  loading: boolean;
  triggerCsvReportDownload: (params?: ExportParams) => void;
}

const PREPARING_TOAST_DELAY_MS = 5000;

export const useCsvReportDownload = ({
  type,
  pollInterval = 750,
  exportParams: initialExportParams,
  timezoneCode = dayjs.tz.guess(),
  onSuccess,
  onError
}: UseCsvReportDownloadOptions): UseCsvReportDownloadValue => {
  const [exportParams, setExportParams] = useState<ExportParams>(null);
  const [csvReportId, setCsvReportId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const preparingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preparingToastShownRef = useRef<boolean>(false);
  const { showToast, dismissToast } = useToaster();

  const clearPreparingTimer = (): void => {
    if (preparingTimerRef.current !== null) {
      clearTimeout(preparingTimerRef.current);
      preparingTimerRef.current = null;
    }
  };

  const clearPreparingState = (): void => {
    clearPreparingTimer();
    if (preparingToastShownRef.current) {
      dismissToast();
      preparingToastShownRef.current = false;
    }
  };

  const triggerCsvReportDownload = useCallback(
    async (params?: ExportParams): Promise<void> => {
      clearPreparingState();
      setLoading(true);
      const resolved = params ?? initialExportParams;
      setExportParams(resolved);
      preparingTimerRef.current = setTimeout(() => {
        preparingToastShownRef.current = true;
        showToast('Preparing export...', { keepOpen: true });
      }, PREPARING_TOAST_DELAY_MS);
      try {
        const data = await generateCSV(type, { exportParams: resolved, timezoneCode });
        setCsvReportId(data.csv_report_id);
      } catch (error) {
        clearPreparingState();
        setLoading(false);
        console.error(error);
        onError?.('Error generating CSV report');
      }
    },
    [initialExportParams, type, timezoneCode, showToast, onError]
  );

  useInterval(
    () => {
      if (!csvReportId) return;

      getCSVReportById(type, { exportParams, csvReportId, timezoneCode }).then(
        ({ status, message }) => {
          if (status === CSV_QUEUE_STATUS.COMPLETED) {
            clearPreparingState();
            setCsvReportId(null);
            setExportParams(null);
            setLoading(false);
            onSuccess?.();
          } else if (status === CSV_QUEUE_STATUS.FAILED) {
            clearPreparingState();
            setCsvReportId(null);
            setExportParams(null);
            setLoading(false);
            onError?.(message);
          }
        }
      );
    },
    csvReportId ? pollInterval : null
  );

  // Clear timer and dismiss toast on unmount to prevent stale state if component unmounts mid-export
  useEffect(() => {
    return () => {
      clearPreparingState();
    };
  }, []);

  return { loading, triggerCsvReportDownload };
};
